import type { JSX } from 'react';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  IChannelBudgets,
  IEphemeralTargetableOrSegment,
  IRecommendedSplit,
  TTDCampaign,
} from '@feathr/blackbox';
import { Button, Pill, Skeleton, toast, Tooltip, Well } from '@feathr/components';
import { DEFAULT_DEBOUNCE_WAIT, errorMessage, getIconForAction, useDebounce } from '@feathr/hooks';
import { RachisError } from '@feathr/rachis';

import * as styles from './RecommendedBudget.css';

interface IProps {
  campaign: TTDCampaign;
  budget: string;
  onClick: () => void;
  recBudget: number | null;
  setRecBudget: (budget: number) => void;
  setMinBudgets: (budgets: IChannelBudgets) => void;
  setRecommendedSplit: (split: IRecommendedSplit) => void;
  setRecommendedAmounts: (amounts: { display: number; meta: number }) => void;
  /** Account's maximum campaign budget, shown as the recommendation when the minimum exceeds it. */
  maxBudget: number;
  /** Flags upward when the campaign's minimum budget exceeds the account maximum. */
  setBudgetExceedsAccountMax: (exceeds: boolean) => void;
  /**
   * Flags upward when a recommendation request fails for a generic reason (not
   * the account-max case). Without a recommendation there are no minimums to
   * validate against, so the wizard blocks rather than letting the user advance
   * with an unvalidated budget. Cleared on a successful re-query.
   */
  setBudgetRecommendationFailed: (failed: boolean) => void;
  /**
   * Whether the campaign has a budget-dependent Display/Meta split (dual-channel
   * Omni). Only then does changing the total budget need a re-query — the
   * recommended budget and minimums are audience/duration-driven, so
   * single-channel campaigns re-fetch only on date/audience changes.
   */
  hasChannelSplit: boolean;
  /**
   * Reports whether a recommendation request is in flight. Lifted so the split
   * can show a loader on its per-channel values and the wizard can block
   * Next/Publish while the split is recomputing.
   */
  setIsRecommendationLoading: (loading: boolean) => void;
  targetingsWithData: IEphemeralTargetableOrSegment[];
}

/**
 * True when the recommendation was rejected because the campaign's minimum
 * budget exceeds the account's maximum campaign budget (the backend returns a
 * `BudgetExceedsAccountMaxError`).
 */
function isBudgetExceedsAccountMaxError(error: unknown): boolean {
  return (
    error instanceof AggregateError &&
    error.errors.some(
      (e) => e instanceof RachisError && !!e.code?.includes('BudgetExceedsAccountMaxError'),
    )
  );
}

function RecommendedBudget({
  budget,
  onClick,
  campaign,
  recBudget,
  setRecBudget,
  setMinBudgets,
  setRecommendedSplit,
  setRecommendedAmounts,
  maxBudget,
  setBudgetExceedsAccountMax,
  setBudgetRecommendationFailed,
  setIsRecommendationLoading,
  hasChannelSplit,
  targetingsWithData,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();

  // Monotonic request counter so out-of-order responses can be discarded (see fetch).
  const requestIdRef = useRef(0);

  /*
   * Re-query when the target budget changes too, but debounce it so typing in
   * the budget field doesn't fire a request per keystroke.
   */
  const targetValue = campaign.get('exposure_settings')?.target_value ?? 0;
  const [debouncedTargetValue, setDebouncedTargetValue] = useDebounce<number>(
    targetValue,
    DEFAULT_DEBOUNCE_WAIT,
  );
  useEffect(() => {
    setDebouncedTargetValue(targetValue);
  }, [targetValue, setDebouncedTargetValue]);

  /*
   * Track the inputs from the previous run so we can tell a budget-only change
   * apart from a date/audience change. A budget change only matters when there's
   * a budget-dependent split to recompute (dual-channel Omni); otherwise the
   * recommended budget is unchanged, so re-querying would just be a wasteful
   * duplicate hit (e.g. the auto-fill from the first response bumping the budget
   * off $0). The date-keyed string covers both dates at once.
   */
  const dateStart = campaign.get('date_start');
  const dateEnd = campaign.get('date_end');
  const prevInputsRef = useRef({
    datesKey: `${dateStart}|${dateEnd}`,
    audienceCount: targetingsWithData.length,
    budget: debouncedTargetValue,
  });

  // Re-query the recommended budget when the campaign's dates or budget change
  useEffect(() => {
    const datesKey = `${dateStart}|${dateEnd}`;
    const audienceCount = targetingsWithData.length;
    const prev = prevInputsRef.current;
    const onlyBudgetChanged =
      debouncedTargetValue !== prev.budget &&
      datesKey === prev.datesKey &&
      audienceCount === prev.audienceCount;
    prevInputsRef.current = { datesKey, audienceCount, budget: debouncedTargetValue };

    // A budget-only change with no split to recompute has nothing to fetch.
    if (onlyBudgetChanged && !hasChannelSplit) {
      return;
    }

    async function getRecommendedBudget(): Promise<void> {
      if (!campaign.isPending && targetingsWithData.length > 0) {
        /*
         * Tag each request so out-of-order responses can't clobber a newer one.
         * Rapid date/budget changes fire overlapping fetches with no cancellation
         * at the endpoint, so a slower earlier response could otherwise overwrite
         * the latest budget/split/minimums. Only the most recent request applies.
         */
        requestIdRef.current += 1;
        const requestId = requestIdRef.current;
        const isStale = (): boolean => requestId !== requestIdRef.current;
        try {
          setIsRecommendationLoading(true);
          const budget = await campaign.getRecommendedBudget({
            campaign: campaign.attributes,
            targetings: targetingsWithData,
          });
          if (isStale()) {
            return;
          }
          setRecBudget(budget.recommended_budget);
          setBudgetExceedsAccountMax(false);
          setBudgetRecommendationFailed(false);
          setMinBudgets({
            display: budget.channels.display.minimum,
            meta: budget.channels.meta.minimum,
            total: budget.minimum_budget,
          });
          setRecommendedSplit({
            display: budget.channels.display.percent,
            meta: budget.channels.meta.percent,
          });
          setRecommendedAmounts({
            display: budget.channels.display.amount,
            meta: budget.channels.meta.amount,
          });
        } catch (error) {
          if (isStale()) {
            return;
          }
          if (isBudgetExceedsAccountMaxError(error)) {
            /*
             * The campaign's minimum budget exceeds the account maximum, so the
             * backend can't recommend one. Cap the displayed budget to the
             * account maximum and flag it upward so the step warns and blocks.
             */
            setRecBudget(maxBudget);
            setBudgetExceedsAccountMax(true);
          } else {
            /*
             * A generic failure means we have no minimums to validate against.
             * Flag it so the wizard blocks rather than silently letting the user
             * advance with an unvalidated (possibly sub-minimum) budget. A later
             * successful re-query clears it.
             */
            setBudgetRecommendationFailed(true);
          }
          // Prefer the backend's own message (e.g. "$37 is less than the minimum required
          // budget of $38 for the campaign"); fall back to the generic copy only when the
          // error carries no usable message.
          toast(errorMessage(error, t('Something went wrong while trying to recommend a budget')), {
            type: 'error',
          });
        } finally {
          if (!isStale()) {
            setIsRecommendationLoading(false);
          }
        }
      }
    }

    getRecommendedBudget();
  }, [
    campaign.get('date_start'),
    campaign.get('date_end'),
    targetingsWithData.length,
    debouncedTargetValue,
  ]);

  /*
   * Only skeleton on the initial load (before any recommendation exists). A
   * later re-query — e.g. the user edited the total budget — doesn't change the
   * recommended budget itself (it's driven by audience/duration), only the
   * split, so keep showing the recommended value while the split reloads.
   */
  if (recBudget === null) {
    return (
      <Well className={styles.root} layout={'vertical'}>
        <Skeleton height={24} width={'80%'} />
        <Skeleton height={32} width={'50%'} />
        <Skeleton height={32} />
      </Well>
    );
  }

  return (
    <Well className={styles.root} layout={'vertical'}>
      <Pill
        className={styles.pill}
        suffix={
          <Tooltip
            title={t(
              "The recommended budget is produced by analyzing your campaign's target audience, filters, optimization settings and duration.",
            )}
          >
            {getIconForAction('info')}
          </Tooltip>
        }
        theme={'cyan'}
        truncate={false}
      >
        {t('Recommended for you')}
      </Pill>
      <span className={styles.bigNumber} data-name={'recommended_budget'}>
        {budget}
      </span>
      <Button name={'use_recommended_budget'} onClick={onClick} type={'primary'}>
        {t('Use recommended budget')}
      </Button>
    </Well>
  );
}

export default RecommendedBudget;
